import { useMemo, useState } from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import {
    colors, fontSizes, fontWeights, forms, lineHeights, space
} from 'style/config';
import Button from 'components/Button/Button.f';
import Divider from 'components/Divider/Divider.f';
import Link from 'components/Link/Link.f';
import Radio from 'components/Inputs/Radio/Radio';
import FormValidator from 'utils/FormValidator';
import PasswordRevealInput from 'components/Inputs/PasswordRevealInput/PasswordRevealInput.es6';
import store from 'Store';
import Actions from 'Actions';
import urlUtils from 'utils/Url';
import { TEXT_TERM_LINK, PRIVACY_POLICY_LINK, TERMS_OF_USE_LINK } from 'constants/legal';

const { wrapFunctionalComponent } = framework;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const noop = () => {};

// Available-auth-method identifiers from the OI-4 authenticationMethods lookup
// (api-contracts §1). Compared case-insensitively so an UPPERCASE producer
// (CredentialEntryScreen emits 'EMAIL'/'PHONE') never breaks matching (F-001).
const METHOD_OTP_SMS = 'OTP_SMS';
const METHOD_OTP_EMAIL = 'OTP_EMAIL';
const METHOD_PASSWORD = 'USERNAME_PASSWORD';

// mfaMode values committed at the SELECT seam (handleSelectMethod in SignIn.f.jsx):
// 'phone'/'email' seed the OTP session + advance to entry; 'password' is the
// password branch (currently a no-op placeholder — preserved). The phone method's
// canonical mfaMode is 'phone' (the Phase-3 backend contract), NOT 'sms' (FIX 4) —
// only DISPLAY copy still distinguishes phone-vs-email.
const MODE_PHONE = 'phone';
const MODE_EMAIL = 'email';
const MODE_PASSWORD = 'password';

const hasMethod = (methods, target) => Array.isArray(methods) && methods.some(m => String(m).toUpperCase() === target);

// The Edit affordance is a link (not a button) so its "email"-bearing aria-label
// never collides with a method OPTION in role/name queries; the click is
// intercepted (preventDefault) and routed via goTo — the href is only a graceful
// no-JS fallback back to the sign-in entry point.
const SIGNIN_ROUTE = '/signin';

// Display the credential in the header row. Normally we show the RAW value the
// customer typed: phone gets NANP formatting ((xxx) xxx-xxxx), email is shown
// verbatim (Figma 270-84177 shows the entered value, not the masked destination).
//
// AC-AUTH-REFRESH (SIM-7 / ACS-4131): after a page refresh on the authorization
// step the raw enteredCredential is intentionally NOT restored — it is PII and is
// never persisted to sessionStorage (BR-021). In that case we fall back to the
// non-PII MASKED forms that ARE persisted: maskedEmail for email, and the masked
// phone `(***) ***-{phoneLast4Digits}` for phone. The masked fallback keeps the
// header meaningful across a refresh without ever storing PII.
const credentialDisplay = ({ enteredCredential, credentialType, maskedEmail, phoneLast4Digits }) => {
    const isPhone = String(credentialType).toUpperCase() === 'PHONE';

    if (enteredCredential) {
        if (isPhone) {
            return FormValidator.getFormattedPhoneNumber(String(enteredCredential)) || enteredCredential;
        }

        return enteredCredential;
    }

    // Refresh-restore: no raw credential available -> masked, non-PII fallback.
    if (isPhone) {
        return phoneLast4Digits ? `(***) ***-${phoneLast4Digits}` : '';
    }

    return maskedEmail || '';
};

/**
 * AuthorizationScreen (SIM-7 / ACS-4131, Phase 2 — Figma node 270-84177).
 *
 * OTP-first authorization for a single resolved account, presented as a RADIO
 * group + a single "Send" button (Figma 270-84177). The options render in the
 * fixed priority order SMS -> Email -> Password (BR-007 / AC-005); each is gated
 * by eligibility (BR-008): SMS only when OTP_SMS ∈ methods AND a phone last-4
 * exists, Email when OTP_EMAIL, Password when USERNAME_PASSWORD.
 *
 * The FIRST eligible option is selected by default. Radios only change the LOCAL
 * selection; nothing is committed until the customer presses "Send", which calls
 * onSelectMethod(selectedMode) — keeping the OTP send in one place (OtpEntryScreen
 * for otp modes; the password branch for password). The SMS-consent copy (with
 * functional Text Terms + Privacy Policy links, BR-033) renders ONLY beneath the
 * SMS option.
 *
 * An "Edit" affordance returns to credential entry (goTo('credential')) WITHOUT
 * issuing a new lookup or OTP send and WITHOUT touching the OTP session counters
 * (BR-041 / BR-025) — it never calls otpSession. No "passcode" terminology
 * anywhere; "verification code" is used (BR-034).
 *
 * Presentation-only: navigation via the injected goTo (owned by the SignIn page's
 * single useAuthFlow instance); method selection is delegated to onSelectMethod.
 *
 * @param {object} props
 * @param {(screen: string) => void} [props.goTo]
 * @param {string[]} props.methods - availableAuthMethods from the lookup.
 * @param {string} [props.phoneLast4Digits]
 * @param {string} [props.maskedEmail] - masked email fallback used for the header
 *   display after a refresh-restore, when the raw enteredCredential (PII) is absent.
 * @param {string} [props.enteredCredential]
 * @param {'EMAIL'|'PHONE'} [props.credentialType]
 * @param {(mfaMode: 'phone'|'email'|'password', extra?: {password: string}) => void} [props.onSelectMethod]
 *   For the password branch (SIM-9, Figma 252-79196) the entered password is passed
 *   as the second arg so SignIn.f.jsx can route it into completeAuthentication.
 */
const AuthorizationScreen = ({
    goTo = noop, methods, phoneLast4Digits, maskedEmail, enteredCredential, credentialType, onSelectMethod = noop
}) => {
    // Eligible options in the fixed OTP-first order (BR-007 / AC-005). Building the
    // list once keeps the default-selection and render loop in lock-step.
    const options = useMemo(() => {
        const list = [];

        if (hasMethod(methods, METHOD_OTP_SMS) && Boolean(phoneLast4Digits)) {
            list.push({ mode: MODE_PHONE, label: getText('smsMethodLabel'), hasConsent: true });
        }

        if (hasMethod(methods, METHOD_OTP_EMAIL)) {
            list.push({ mode: MODE_EMAIL, label: getText('emailMethodLabel'), hasConsent: false });
        }

        if (hasMethod(methods, METHOD_PASSWORD)) {
            list.push({ mode: MODE_PASSWORD, label: getText('passwordMethodLabel'), hasConsent: false });
        }

        return list;
    }, [methods, phoneLast4Digits]);

    // Default selection = FIRST eligible option (Figma: SMS if eligible, else
    // Email, else Password). Local state; only "Send" commits it.
    const [selectedMode, setSelectedMode] = useState(() => options[0]?.mode);
    // SIM-9 inline password (Figma 252-79196): local password value + an
    // empty-submit validation flag. The raw password lives ONLY in this ref-backed
    // local state and is never logged or persisted (BR-021).
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const isPasswordMode = selectedMode === MODE_PASSWORD;

    const goToCredential = () => goTo('credential');

    // Switching the selected method clears any inline password-validation state so
    // the error does not linger when the customer moves to an OTP option.
    const handleSelect = mode => {
        setSelectedMode(mode);
        setPasswordError(false);
    };

    // Forgot Password? (Figma 252-79196): open the existing forgot-password modal via
    // the Store singleton (Auth pattern). The modal uses an email-only InputEmail, so
    // only pre-fill when the entered credential is actually an email — a phone-entered
    // credential would fail the modal's email validation, so it opens with no prefill.
    const handleForgotPassword = () => {
        const isEmailCredential = String(credentialType).toUpperCase() === 'EMAIL';
        store.dispatch(Actions.showForgotPasswordModal(true, isEmailCredential ? enteredCredential : ''));
    };

    const handleSend = () => {
        if (!selectedMode) {
            return;
        }

        if (isPasswordMode) {
            // Empty password blocks submit with an inline error; no branch commit.
            if (!password) {
                setPasswordError(true);

                return;
            }

            onSelectMethod(MODE_PASSWORD, { password });

            return;
        }

        onSelectMethod(selectedMode);
    };

    return (
        <div css={styles.root}>
            {/* Header block (Figma 270-84177): the entered credential + an Edit link
                side by side. The single "Sign In" page <h1> (SignIn.f.jsx) is the
                heading — this screen intentionally renders NO heading of its own to
                avoid a duplicate "Sign In" (the radiogroup's aria-label is this
                screen's semantic label). No back chevron in this frame — Edit is the
                return-to-credential affordance and it does not re-lookup/send
                (AC-AUTH-EDIT / AC-AUTH-BACK). */}
            <div css={styles.header}>
                <div css={styles.credentialRow}>
                    <span css={styles.credential}>{credentialDisplay({ enteredCredential, credentialType, maskedEmail, phoneLast4Digits })}</span>
                    {/* Design-system Link (renders a button with a span aria role, so
                        its aria-label — which necessarily contains "email" — does not
                        collide with a method OPTION when queries filter by /email/i).
                        href is a graceful no-JS fallback; the click is intercepted and
                        routed via goTo. */}
                    <Link
                        href={SIGNIN_ROUTE}
                        color='link'
                        underline={true}
                        aria-label={getText('editAriaLabel')}
                        onClick={event => {
                            event.preventDefault();
                            goToCredential();
                        }}
                    >
                        {getText('editLabel')}
                    </Link>
                </div>
            </div>

            <Divider css={styles.divider} />

            {/* Radio option group in fixed OTP-first order (BR-007 / AC-005). Reuses
                the design-system Radio (components/Inputs/Radio) — it owns the dot,
                the hidden role="radio" input, label association, and focus ring. */}
            <div
                role='radiogroup'
                aria-label={getText('authorizationSubtitle')}
                css={styles.radioGroup}
            >
                {options.map(option => (
                    <div key={option.mode}>
                        <Radio
                            id={`authMethod-${option.mode}`}
                            name='authMethod'
                            value={option.mode}
                            checked={option.mode === selectedMode}
                            onChange={() => handleSelect(option.mode)}
                            alignItems='flex-start'
                            paddingY={null}
                        >
                            {option.label}
                        </Radio>
                        {/* SMS-consent copy directly below the SMS option only
                            (BR-033 / AC-AUTH-SMSCONSENT); rendered OUTSIDE the Radio
                            label so it is not part of the clickable control. */}
                        {option.hasConsent && (
                            <p css={styles.consent}>
                                {getText('smsConsentPrefix')}
                                <Link
                                    href={urlUtils.getLink(TEXT_TERM_LINK)}
                                    color='link'
                                    underline={true}
                                    css={styles.consentLink}
                                >
                                    {getText('smsConsentTextTerms')}
                                </Link>
                                {getText('smsConsentMiddle')}
                                <Link
                                    href={urlUtils.getLink(PRIVACY_POLICY_LINK)}
                                    color='link'
                                    underline={true}
                                    css={styles.consentLink}
                                >
                                    {getText('smsConsentPrivacy')}
                                </Link>
                                {getText('smsConsentSuffix')}
                            </p>
                        )}
                    </div>
                ))}
            </div>

            {/* Inline password branch (Figma 252-79196): revealed only when the
                "Enter password" radio is selected. Reuses the design-system
                PasswordRevealInput (show/hide eye built in) with name/id "password"
                and a floating label for its accessible name (TEST-REV-001). The
                Forgot Password? affordance is the shared Link (a real <a>), and the
                SMS consent is replaced by the password-consent copy. */}
            {isPasswordMode && (
                <div css={styles.passwordBlock}>
                    <PasswordRevealInput
                        id='password'
                        name='password'
                        label={getText('passwordFieldLabel')}
                        aria-label={getText('passwordFieldLabel')}
                        value={password}
                        onChange={event => {
                            setPassword(event?.target?.value ?? '');
                            setPasswordError(false);
                        }}
                    />
                    {passwordError && (
                        <div
                            role='alert'
                            aria-live='assertive'
                            css={styles.passwordError}
                        >
                            {getText('passwordRequired')}
                        </div>
                    )}
                    <div css={styles.forgotRow}>
                        <Link
                            href={SIGNIN_ROUTE}
                            color='link'
                            underline={true}
                            onClick={event => {
                                event.preventDefault();
                                handleForgotPassword();
                            }}
                        >
                            {getText('forgotPasswordLink')}
                        </Link>
                    </div>
                    <p css={styles.passwordConsent}>
                        {getText('passwordConsentPrefix')}
                        <Link
                            href={urlUtils.getLink(TERMS_OF_USE_LINK)}
                            color='link'
                            underline={true}
                            css={styles.consentLink}
                        >
                            {getText('passwordConsentTerms')}
                        </Link>
                        {getText('passwordConsentMiddle')}
                        <Link
                            href={urlUtils.getLink(PRIVACY_POLICY_LINK)}
                            color='link'
                            underline={true}
                            css={styles.consentLink}
                        >
                            {getText('passwordConsentPrivacy')}
                        </Link>
                        {getText('passwordConsentSuffix')}
                    </p>
                </div>
            )}

            <Button
                variant='primary'
                block={true}
                onClick={handleSend}
                css={styles.send}
            >
                {isPasswordMode ? getText('signInButton') : getText('sendButton')}
            </Button>
        </div>
    );
};

const styles = {
    // 400px column, 16px gap between blocks (Figma 270-84177).
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[4]
    },
    // Header block: 8px gap between the heading and the credential/Edit row.
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2]
    },
    credentialRow: {
        display: 'flex',
        alignItems: 'center',
        gap: space[2]
    },
    credential: {
        color: colors.black,
        fontSize: fontSizes.base
    },
    // Full-width 1px #EEE rule (colors.lightGray).
    divider: {
        margin: 0,
        borderColor: colors.lightGray
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[4]
    },
    // SMS-consent copy: 12px #666, 14px line-height; the two legal anchors are
    // UPPERCASE (via textTransform), bold, blue #136BEA, underlined.
    consent: {
        color: colors.gray,
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        marginTop: space[2],
        // Align under the Radio label (past the dot + its right margin).
        marginLeft: forms.RADIO_SIZE + forms.RADIO_MARGIN,
        marginBottom: 0
    },
    // Bold + UPPERCASE deltas on top of the shared Link (which supplies the blue
    // colour + underline via its color/underline props).
    consentLink: {
        fontWeight: fontWeights.bold,
        textTransform: 'uppercase'
    },
    // Inline password branch container (Figma 252-79196): stacks the field, the
    // right-aligned Forgot Password link and the password-consent copy.
    passwordBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2]
    },
    // Empty-password inline validation (colors.error, 12px), mirroring the
    // credential-entry error treatment.
    passwordError: {
        color: colors.error,
        fontSize: fontSizes.sm
    },
    // Right-align the Forgot Password link under the field (Figma 252-79196).
    forgotRow: {
        display: 'flex',
        justifyContent: 'flex-end'
    },
    // Password-consent copy (Figma 252-79196): 12px #666 like the SMS consent but
    // full-width (not indented under a radio dot).
    passwordConsent: {
        color: colors.gray,
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        marginTop: 0,
        marginBottom: 0
    },
    send: {
        marginTop: 0
    }
};

export default wrapFunctionalComponent(AuthorizationScreen, 'AuthorizationScreen');
